import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Spinner } from "reactstrap";
import useApiCallService from "../../Common/Helpers/ApiCall";
import { FlashMessage } from "../../Common/Helpers/FlashMessage";
import { SlidePanel } from "../../Common/Components/Modal/SlidePanel";

const EMPTY_VAT = { name: '', vatValue: '', accounting: '', momsfritt: false };

/**
 * VatFormPanel
 *
 * Centered SlidePanel to create or edit an invoice account / VAT configuration,
 * using the same UI as the other system settings panels.
 * The API calls and validation rules are the same as in the former VatManagementModal.
 *
 * Props:
 *   isOpen   boolean
 *   vat      object | null   null = create, otherwise the VAT configuration to edit
 *   onClose  () => void
 *   onSaved  () => void      called after a successful save
 */
export function VatFormPanel({ isOpen, vat = null, onClose, onSaved }) {
    const { t } = useTranslation();
    const { ApiCall } = useApiCallService();

    const isEdit = !!vat;
    const [form, setForm] = useState(EMPTY_VAT);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [vatValueMaxError, setVatValueMaxError] = useState(false);

    useEffect(() => {
        setVatValueMaxError(false);
        if (!isOpen) {
            setForm(EMPTY_VAT);
            setIsSubmitting(false);
            return;
        }
        setForm(vat
            ? {
                id: vat.id,
                name: vat.name,
                vatValue: vat.vatValue,
                accounting: vat.accounting || '',
                momsfritt: vat.momsfritt || false,
            }
            : EMPTY_VAT);
    }, [isOpen, vat]);

    const onlyDigits = (value) => value.replace(/[^0-9]/g, '');

    const handleSave = async () => {
        // Same rules as the former VatManagementModal
        if (!form.name || (!form.momsfritt && !form.vatValue)) {
            FlashMessage(t('validationMessage.requiredFields'), 'flash-messageError');
            return;
        }
        if (isEdit && !form.id) {
            FlashMessage(t('validationMessage.missingVatId'), 'flash-messageError');
            return;
        }

        const vatValueNum = form.momsfritt ? 0 : parseInt(form.vatValue);
        if (!form.momsfritt && (isNaN(vatValueNum) || vatValueNum < 0)) {
            FlashMessage(t('validationMessage.invalidVatValue'), 'flash-messageError');
            return;
        }
        // VAT is a percentage: it cannot exceed 100 %
        if (!form.momsfritt && vatValueNum > 100) {
            setVatValueMaxError(true);
            return;
        }

        const apiCallOptions = isEdit
            ? {
                method: "put",
                url: `/invoice/vat/update/${form.id}`,
                data: {
                    name: form.name.trim(),
                    vatValue: vatValueNum,
                    accounting: form.accounting ? form.accounting.trim() : '',
                    momsfritt: form.momsfritt,
                },
            }
            : {
                method: "post",
                url: `/invoice/vat/save?name=${encodeURIComponent(form.name.trim())}&vatValue=${vatValueNum}${form.accounting ? `&accounting=${encodeURIComponent(form.accounting.trim())}` : ''}&momsfritt=${form.momsfritt}`,
            };

        setIsSubmitting(true);
        try {
            await ApiCall(apiCallOptions, (response) => {
                if (response && response.success) {
                    FlashMessage(
                        isEdit
                            ? (t('validationMessage.updateVatSuccess') || 'VAT updated successfully')
                            : (t('validationMessage.saveSuccess') || 'VAT saved successfully'),
                        'flash-messageSuccess'
                    );
                    onClose();
                    if (onSaved) onSaved();
                } else {
                    FlashMessage(isEdit ? t('validationMessage.updateError') : 'Error saving VAT configuration', 'flash-messageError');
                }
                setIsSubmitting(false);
            });
        } catch (error) {
            console.error('Error saving VAT:', error);
            FlashMessage(t('validationMessage.sendError') || 'Error saving VAT', 'flash-messageError');
            setIsSubmitting(false);
        }
    };

    const canSave = !!form.name && (form.momsfritt || !!form.vatValue) && !isSubmitting;

    return (
        <SlidePanel
            isOpen={isOpen}
            onClose={onClose}
            title={isEdit ? t('button.edit') : t('invoice.addNewVat')}
            variant="center"
        >
            <div className="select-org-panel-wrapper">
                {/* Account number: digits only, max 4 */}
                <div className="mb-3">
                    <label htmlFor="vatAccounting" className="select-org-panel-label">{t('invoice.accounting') || 'Kontonummer'}</label>
                    <input
                        id="vatAccounting"
                        type="text"
                        inputMode="numeric"
                        className="form-control"
                        value={form.accounting}
                        onChange={(e) => setForm({ ...form, accounting: onlyDigits(e.target.value).slice(0, 4) })}
                        placeholder={t('invoice.enterAccounting') || 'Ange kontonummer'}
                        maxLength={4}
                    />
                </div>

                {/* Account name */}
                <div className="mb-3">
                    <label htmlFor="vatName" className="select-org-panel-label">{t('invoice.accountName')}</label>
                    <input
                        id="vatName"
                        type="text"
                        className="form-control"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        placeholder={t('invoice.enterAccountName')}
                    />
                </div>

                {/* VAT value: digits only, disabled when VAT free */}
                <div className="mb-3">
                    <label htmlFor="vatValue" className="select-org-panel-label">{t('invoice.vatValue')}</label>
                    <input
                        id="vatValue"
                        type="text"
                        inputMode="numeric"
                        className="form-control"
                        value={form.momsfritt ? '0' : form.vatValue}
                        onChange={(e) => {
                            if (!form.momsfritt) setForm({ ...form, vatValue: onlyDigits(e.target.value) });
                            setVatValueMaxError(false);
                        }}
                        placeholder={t('invoice.enterVatPercentage')}
                        disabled={form.momsfritt}
                    />
                    {vatValueMaxError && (
                        <div className="mt-1" style={{ color: '#f56e6e', fontSize: '12px' }}>{t('economic.maxPercent')}</div>
                    )}
                </div>

                {/* VAT free */}
                <div className="rpv2-multiday-row mb-4">
                    <input
                        type="checkbox"
                        id="vatMomsfritt"
                        className="rpv2-multiday-checkbox"
                        checked={!!form.momsfritt}
                        onChange={(e) => {
                            const isMomsfritt = e.target.checked;
                            setForm({ ...form, momsfritt: isMomsfritt, vatValue: isMomsfritt ? '0' : '' });
                            setVatValueMaxError(false);
                        }}
                    />
                    <label htmlFor="vatMomsfritt" className="rpv2-multiday-label">
                        {t('invoice.momsfritt') || 'Momsfritt'}
                    </label>
                </div>

                {/* Buttons */}
                <div className="select-org-panel-buttons">
                    <button type="button" onClick={onClose} className="select-org-panel-btn-cancel">
                        {t('button.cancel') || "Avbryt"}
                    </button>
                    <button
                        type="button"
                        onClick={handleSave}
                        disabled={!canSave}
                        className={`select-org-panel-btn-confirm ${canSave ? "enabled" : "disabled"}`}
                    >
                        {isSubmitting
                            ? <Spinner size="sm" />
                            : (isEdit ? t('button.save') : (t('button.addVat') || t('button.create')))}
                    </button>
                </div>
            </div>
        </SlidePanel>
    );
}
